import {
  convertMinor,
  isPaid,
  type ComputeResult,
  type Move,
  type Pence,
  type VulpariNode,
} from "@vulpari/core";
import { useMemo, useState } from "react";

import { formatMoney } from "../lib/format.js";
import { groupTotal, incomingInPrimary } from "../lib/groupTotal.js";
import { useStore } from "../store.js";

/** Node types that can be ticked off as paid. */
const PAYABLE = new Set<VulpariNode["type"]>(["bill", "card", "oneoff", "pot", "external"]);

interface Props {
  /** Nodes with balances carried forward to the viewed month (matches canvas). */
  nodes: VulpariNode[];
  result: ComputeResult;
}

interface MoveGroup {
  sourceId: string;
  moves: Move[];
  total: number;
}

/** Whether a move brings money in, sends it out, or just shuffles it between
 * your own accounts. Mirrors the canvas connector colours. */
type Direction = "in" | "out" | "transfer";

const DIR_AMOUNT: Record<Direction, string> = {
  in: "text-positive",
  out: "text-accent",
  transfer: "text-ink-muted",
};

const DIR_SIGN: Record<Direction, string> = {
  in: "+",
  out: "-",
  transfer: "",
};

export function SafeToSpend({ nodes, result }: Props) {
  const groups = useStore((state) => state.groups);
  const primaryCurrency = useStore((state) => state.context.primaryCurrency) ?? "GBP";
  const rates = useStore((state) => state.context.rates);
  const month = useStore((state) => state.context.month);

  // Which payday-move accounts are collapsed (by source node id).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of nodes) {
      map.set(node.id, node.name);
    }
    return map;
  }, [nodes]);

  const typeById = useMemo(() => {
    const map = new Map<string, VulpariNode["type"]>();
    for (const node of nodes) {
      map.set(node.id, node.type);
    }
    return map;
  }, [nodes]);

  // A move's amount is in its source's currency, so it is shown in that currency.
  const currencyById = useMemo(() => {
    const map = new Map<string, string>();
    for (const node of nodes) {
      map.set(node.id, node.currency);
    }
    return map;
  }, [nodes]);
  const currencyOf = (id: string) => currencyById.get(id) ?? primaryCurrency;

  const directionOf = (sourceId: string, targetId: string): Direction => {
    if (typeById.get(sourceId) === "income") {
      return "in"; // income arriving
    }
    if (typeById.get(targetId) === "account") {
      return "transfer"; // moved between your own accounts
    }
    return "out"; // leaving for a bill, card, pot or payee
  };

  // Group totals: the money flowing into each group's members this month (the
  // monthly cost of that category), in the primary currency.
  const groupTotals = useMemo(() => {
    const incoming = incomingInPrimary(
      result.moves,
      (id) => currencyById.get(id) ?? primaryCurrency,
      primaryCurrency,
      rates,
    );
    return groups
      .filter((group) => group.nodeIds.length > 0)
      .map((group) => ({
        id: group.id,
        name: group.name,
        total: groupTotal(group.nodeIds, incoming),
      }));
  }, [groups, result.moves, currencyById, primaryCurrency, rates]);

  // Payday moves grouped by the account (or income) they come from, in the
  // order they first appear.
  const moveGroups = useMemo(() => {
    const order: string[] = [];
    const bySource = new Map<string, MoveGroup>();
    for (const move of result.moves) {
      let group = bySource.get(move.fromNodeId);
      if (!group) {
        group = { sourceId: move.fromNodeId, moves: [], total: 0 };
        bySource.set(move.fromNodeId, group);
        order.push(move.fromNodeId);
      }
      group.moves.push(move);
      group.total += move.amount;
    }
    return order.map((id) => bySource.get(id)!);
  }, [result.moves]);

  // Money still to pay this month: unpaid obligations, in the primary currency.
  const remaining = useMemo(() => {
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    let total = 0;
    for (const move of result.moves) {
      const target = nodeById.get(move.toNodeId);
      if (!target || !PAYABLE.has(target.type) || isPaid(target, month)) {
        continue;
      }
      const source = nodeById.get(move.fromNodeId);
      total += convertMinor(
        move.amount as Pence,
        source?.currency ?? primaryCurrency,
        primaryCurrency,
        rates,
      );
    }
    return total;
  }, [nodes, result.moves, month, primaryCurrency, rates]);

  const isShort = result.safeToSpend < 0;

  return (
    <aside className="pointer-events-auto absolute right-4 top-4 flex max-h-[calc(100%-2rem)] w-80 flex-col gap-4 rounded-2xl border border-node-border bg-node-bg/95 p-5 shadow-2xl backdrop-blur">
      <div>
        <p className="text-sm uppercase tracking-wide text-ink-muted">Safe to spend</p>
        <p
          className={`figure mt-1 text-4xl font-bold ${isShort ? "text-warning" : "text-positive"}`}
        >
          {formatMoney(result.safeToSpend, primaryCurrency)}
        </p>
        {remaining > 0 ? (
          <p className="mt-1 text-xs text-ink-muted">
            <span className="figure text-accent">{formatMoney(remaining, primaryCurrency)}</span>{" "}
            still to pay this month
          </p>
        ) : null}
      </div>

      {/* Everything below the headline scrolls, so a long list never runs off
          the screen. */}
      <div className="scroll-slim -mr-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-2">
        {result.warnings.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {result.warnings.map((warning, index) => (
              <li
                key={`${warning.code}-${index}`}
                className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-ink"
              >
                {warning.message}
              </li>
            ))}
          </ul>
        ) : null}

        {groupTotals.length > 0 ? (
          <div>
            <p className="mb-2 text-sm uppercase tracking-wide text-ink-muted">By group</p>
            <ol className="flex flex-col gap-1.5">
              {groupTotals.map((group) => (
                <li key={group.id} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-ink-muted">{group.name}</span>
                  <span className="figure text-ink">
                    {formatMoney(group.total, primaryCurrency)}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        <div>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <p className="text-sm uppercase tracking-wide text-ink-muted">Payday moves</p>
            <p className="flex gap-2 text-xs">
              <span className="text-positive">+ in</span>
              <span className="text-accent">- out</span>
            </p>
          </div>
          {moveGroups.length === 0 ? (
            <p className="text-sm text-ink-muted">No moves yet. Wire some flows together.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {moveGroups.map((group) => {
                // Only accounts collapse into a section; income, one-offs and the
                // like are each shown as a plain row.
                if (typeById.get(group.sourceId) !== "account") {
                  return group.moves.map((move) => {
                    const dir = directionOf(move.fromNodeId, move.toNodeId);
                    return (
                      <div
                        key={move.flowId}
                        className="flex items-baseline justify-between gap-2 rounded-lg border border-node-border px-3 py-2 text-sm"
                      >
                        <span className="text-ink-muted">
                          {nameById.get(move.fromNodeId) ?? "?"} to{" "}
                          {nameById.get(move.toNodeId) ?? "?"}
                        </span>
                        <span className={`figure ${DIR_AMOUNT[dir]}`}>
                          {DIR_SIGN[dir]}
                          {formatMoney(move.amount, currencyOf(move.fromNodeId))}
                        </span>
                      </div>
                    );
                  });
                }

                const isCollapsed = collapsed.has(group.sourceId);
                return (
                  <div key={group.sourceId} className="rounded-lg border border-node-border">
                    <button
                      type="button"
                      onClick={() => toggle(group.sourceId)}
                      aria-expanded={!isCollapsed}
                      className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-canvas"
                    >
                      <span className="flex items-baseline gap-1.5 text-ink">
                        <span className="text-ink-muted">{isCollapsed ? "▸" : "▾"}</span>
                        {nameById.get(group.sourceId) ?? "?"}
                      </span>
                      <span className="figure text-ink-muted">
                        {formatMoney(group.total, currencyOf(group.sourceId))}
                      </span>
                    </button>
                    {isCollapsed ? null : (
                      <ol className="flex flex-col gap-1.5 border-t border-node-border px-3 py-2">
                        {group.moves.map((move) => {
                          const dir = directionOf(move.fromNodeId, move.toNodeId);
                          return (
                            <li
                              key={move.flowId}
                              className="flex items-baseline justify-between gap-2 text-sm"
                            >
                              <span className="text-ink-muted">
                                {nameById.get(move.toNodeId) ?? "?"}
                              </span>
                              <span className={`figure ${DIR_AMOUNT[dir]}`}>
                                {DIR_SIGN[dir]}
                                {formatMoney(move.amount, currencyOf(move.fromNodeId))}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
